import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import { cn } from '@goaltree/ui/lib/utils'
import { useMutation, useQuery } from 'convex/react'
import { Check, Handshake, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'

type Commitments = NonNullable<ReturnType<typeof useCommitments>>
type Commitment = Commitments['open'][number]

export function useCommitments(enabled = true) {
  return useQuery(api.coachCommitments.list, enabled ? {} : 'skip')
}

// "Tue, Thu" for this week's days, with a date once they're further out.
export function commitmentDays(dueDays: string[], lang: string) {
  const week = Date.now() + 6 * 86_400_000
  return dueDays
    .map((d) => {
      const date = new Date(`${d}T12:00:00`)
      return date.toLocaleDateString(
        lang,
        date.getTime() < week
          ? { weekday: 'short' }
          : { weekday: 'short', month: 'short', day: 'numeric' },
      )
    })
    .join(', ')
}

export function useRemoveCommitment() {
  return useMutation(api.coachCommitments.remove).withOptimisticUpdate((store, { id }) => {
    const cur = store.getQuery(api.coachCommitments.list, {})
    if (cur)
      store.setQuery(
        api.coachCommitments.list,
        {},
        {
          open: cur.open.filter((c) => c._id !== id),
          recent: cur.recent.filter((c) => c._id !== id),
        },
      )
  })
}

function Row({ c, onRemove }: { c: Commitment; onRemove: (id: Id<'coachCommitments'>) => void }) {
  const { t, i18n } = useTranslation()
  const resolved = c.status !== 'open'
  return (
    <li className="group flex items-start gap-2 px-2.5 py-2">
      <span
        className={cn(
          'mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border',
          c.status === 'kept'
            ? 'border-primary bg-primary text-primary-foreground'
            : c.status === 'missed'
              ? 'border-muted-foreground/40 text-muted-foreground'
              : c.due
                ? 'border-amber-500'
                : 'border-border',
        )}
      >
        {c.status === 'kept' && <Check className="size-3" strokeWidth={3} />}
        {c.status === 'missed' && <X className="size-2.5" strokeWidth={3} />}
      </span>
      <span className="min-w-0 flex-1">
        <span
          className={cn(
            'block text-[13px] font-medium text-foreground',
            c.status === 'missed' && 'text-muted-foreground line-through',
          )}
        >
          {c.text}
        </span>
        <span className="block text-[11px] text-muted-foreground">
          {resolved
            ? c.autoKept
              ? t('commitments.autoKept')
              : t(`commitments.${c.status}`)
            : `${c.due ? `${t('commitments.due')} · ` : ''}${commitmentDays(c.dueDays, i18n.language)}`}
          {c.linked && !resolved ? ` · ${c.linked}` : ''}
        </span>
      </span>
      <button
        type="button"
        onClick={() => onRemove(c._id)}
        aria-label={t('commitments.remove')}
        className="cursor-pointer rounded p-0.5 text-muted-foreground/60 opacity-60 transition-opacity hover:text-foreground group-hover:opacity-100"
      >
        <X className="size-3.5" />
      </button>
    </li>
  )
}

// "Your commitments" in the coach sheet: open ones, then the last 2 weeks.
export function CoachCommitmentsPanel({ className }: { className?: string }) {
  const { t } = useTranslation()
  const data = useCommitments()
  const remove = useRemoveCommitment()
  const onRemove = (id: Id<'coachCommitments'>) => void remove({ id }).catch(() => {})
  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <p className="flex items-center gap-1.5 px-1 text-[13px] font-semibold text-foreground">
        <Handshake className="size-4 text-primary-text" /> {t('commitments.title')}
      </p>
      {data === undefined ? (
        <div className="h-16 animate-pulse rounded-xl bg-muted/40" />
      ) : !data.open.length && !data.recent.length ? (
        <p className="px-1 text-xs leading-relaxed text-muted-foreground">
          {t('commitments.empty')}
        </p>
      ) : (
        <>
          {data.open.length > 0 && (
            <ul className="divide-y divide-border/60 rounded-xl border border-border/60 bg-card">
              {data.open.map((c) => (
                <Row key={c._id} c={c} onRemove={onRemove} />
              ))}
            </ul>
          )}
          {data.recent.length > 0 && (
            <>
              <p className="px-1 pt-1 text-[10px] font-bold uppercase tracking-widest text-muted-foreground/70">
                {t('commitments.recent')}
              </p>
              <ul className="divide-y divide-border/60 rounded-xl border border-border/60 bg-card">
                {data.recent.map((c) => (
                  <Row key={c._id} c={c} onRemove={onRemove} />
                ))}
              </ul>
            </>
          )}
        </>
      )}
    </div>
  )
}
